import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import TextField from '@aura/components/text-field'
import Paper from '@aura/components/paper'
import Typography from '@aura/components/typography'
import Autocomplete from '@aura/components/autocomplete'
import Checkbox from '@aura/components/checkbox'
import AppBar from '@aura/components/app-bar'
import Toolbar from '@aura/components/toolbar'
import clsx from 'clsx'
import { FormLabel } from '@mui/material'
import HorizontalGroup from '@aura/components/horizontal-group'
import Form from '@aura/forms/form'
import Button from '@aura/components/button'
import Grid from '@aura/components/grid'
import { withStyles } from '@aura/components/styles'
import { useTranslation } from 'react-i18next'
import { useAuraThemeSwitcher } from '@aura/components/themes'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import ReCAPTCHA from 'react-google-recaptcha'
import appContext from '../../../../utils/app-context'
import constants from '../../../../constants/languages'
import languageList from '../../../../constants/language-list'
import { ANALYTICS_REGISTER } from '../../../../constants/routes'
import Footer from '../../../../components/footer'
import SubmitSuccess from '../successSubmit'
import LanguageButton from '../../../../components/language-button'
import MenuContext from '../../../../utils/menu-context'
import { isFrench } from '../../../../constants/app'
import formFields, { initialValues } from './constants/form-fields'
import styles from './styles'


const { EMAIL, FIRST_NAME, LAST_NAME, COMPANY_NAME, PHONE_NUMBER, LANGUAGE, INDEX_ANALYTICS, EQUITIES, FX, FUTURES, API, INTERESTED_IN, COUNTRY_CODE, EXT } = formFields
const NO_DATA = 'No results found.'

const Component = ({
    classes,
    errorMessage,
    onRequestPhoneCodes,
    onRequestRecaptcha,
    onSubmitRequest,
    phoneCodes,
    recaptchaValue,
    stepNumber
}) => {

    const { t: translate, i18n } = useTranslation()
    const { updateTheme } = useAuraThemeSwitcher()
    const title = translate('registrationAnalytics.title')
    const verbiage = translate('registrationAnalytics.verbiage')
    const reCaptchaError = translate('recaptchaError')
    const ref = useRef()
    const recaptchaRef = useRef(null)
    const { locale } = useParams()
    const history = useHistory()
    const menuContext = useContext(MenuContext)
    const [ selectedLanguage, setSelectedLanguage ] = useState(i18n.language)
    const [ reCaptcha, setReCaptcha ] = useState(null)
    const [ captchaError, setCaptchaError ] = useState('')
    const [ siteKey, setSiteKey ] = useState(null)
    const [ key, setKey ] = useState(0)
    const { mobileDevice } = menuContext
    const isMobile = window.innerWidth <= 670
    const smallScreen = window.innerWidth > 671 && window.innerWidth <= 1024
    const analyticsPath = window.location.pathname.split('/analytics')[0] + '/analytics/register'
    const previousPath = window.location.pathname.split('/register')[0] + '/login'
    const { application: applicationContext } = useContext(appContext)
    const { appValue, loginTitle } = applicationContext
    const interestedIn = [ { field: INDEX_ANALYTICS, name: 'Index Analytics' }, { field: EQUITIES, name: 'Equities' }, { field: FUTURES, name: 'Futures' }, { field: FX, name: 'FX' }, { field: API, name: 'APIs' } ]
    const [ countryCode, setCountryCode ] = useState('+1')
    const [ countryCodeList, setCountryCodeList ] = useState([])
    const [ defaultCountryCode, setDefaultCountryCode ] = useState('')

    const getOptionLabel = option => option ? translate('language.' + option.label) : ''
    const getOptionLabelList = (option, list) => {
        let matchingRecord = ''

        if (option && list) {
            if (option.value) {
                matchingRecord = list.find(rec => rec.value === option.value)
            } else {
                matchingRecord = list.find(rec => rec.value === option)
            }

            matchingRecord = matchingRecord ? matchingRecord.label : ''
        }

        return matchingRecord
    }
    useEffect(() => {
        const defaultLang = constants.ENGLISH
        if (locale === constants.ENGLISH || locale === constants.FRENCH) {
            setSelectedLanguage(locale)
            i18n.changeLanguage(locale)
        } else if (isFrench(selectedLanguage)) {
            setSelectedLanguage(constants.FRENCH)
            i18n.changeLanguage(constants.FRENCH)
            history.push(analyticsPath + `/${constants.FRENCH}`)
        } else {
            setSelectedLanguage(defaultLang)
            i18n.changeLanguage(defaultLang)
            history.push(analyticsPath + `/${defaultLang}`)
        }
        stepNumber !== 1 && handleFieldError()
        handleExecuteRecaptcha()
        setReCaptcha('')
    }, [ locale ])

    useEffect(() => {
        onRequestRecaptcha()
        onRequestPhoneCodes()
        updateTheme({ palette: 'light' })
    }, [])

    useEffect(() => {
        const { setFieldValue } = ref.current.getContext()
        if (phoneCodes && phoneCodes.length > 0) {
            const formattedPhoneCodes = phoneCodes.map(code => {
                const [ country, ...codeValue ] = code.label.split(' ')
                return {
                    ...code,
                    label: `${codeValue.join(' ')} ${country}`,
                }
            })
            setCountryCodeList(formattedPhoneCodes)
            const defaultCode = formattedPhoneCodes.find(code => code.value === '+1')
            if (defaultCode) {
                setCountryCode(defaultCode.label)
                setDefaultCountryCode(defaultCode.label)
                setFieldValue(COUNTRY_CODE.value, defaultCode)
            }
        }
    }, [ phoneCodes ])

    useEffect(() => {
        const { setFieldValue } = ref.current.getContext()
        const defaultCode = countryCodeList.find(code => code.value === '+1')

        if (countryCode === '') {
            setFieldValue(COUNTRY_CODE.value, defaultCode)
        }
    }, [ countryCode ])


    useEffect(() => {
        if (recaptchaValue) {
            setSiteKey(recaptchaValue.site_key)

            handleExecuteRecaptcha()
            setReCaptcha('')
        }
    }, [ recaptchaValue ])


    const handleSubmit = val => {
        const codeValue = val.countryCode.label.split(' ')
        let phoneNumber = ''
        if (val.ext) {
            phoneNumber = codeValue[0] + val.phoneNumber + 'x' + val.ext
        } else {
            phoneNumber = codeValue[0] + val.phoneNumber
        }
        if (!siteKey) {
            setCaptchaError('')
            onSubmitRequest({
                'appValue': appValue,
                'companyName': val.companyName,
                'email': val.email,
                'firstName': val.firstName,
                'interestedIn': val.interestedIn,
                'lastName': val.lastName,
                'phoneNumber': phoneNumber,
                'preferredLanguage': val.preferredLanguage.value,
            })
        } else {
            if (!reCaptcha) {
                setCaptchaError(reCaptchaError)
            } else {
                setCaptchaError('')
                onSubmitRequest({
                    'appValue': appValue,
                    'companyName': val.companyName,
                    'email': val.email,
                    'firstName': val.firstName,
                    'interestedIn': val.interestedIn,
                    'lastName': val.lastName,
                    'phoneNumber': phoneNumber,
                    'preferredLanguage': val.preferredLanguage.value,
                })
            }
        }
    }


    const handleChangeCheckbox = e => {
        const { setFieldValue, values } = ref.current.getContext()
        const { checked, name } = e.target
        if (checked) {
            setFieldValue(INTERESTED_IN.value, [ ...(values[INTERESTED_IN.value] || []), name ])
        } else {
            setFieldValue(INTERESTED_IN.value, (values[INTERESTED_IN.value] || []).filter(item => item !== name))
        }
    }

    const handleChange = handleChangeCheckbox

    const handlePhoneNumberChange = e => {
        const { setFieldValue, values } = ref.current.getContext()
        const val = /^$|^[0-9-()]+$/.test(e.target.value)
            ? e.target.value : values[PHONE_NUMBER.value]

        setFieldValue(PHONE_NUMBER.value, val)
    }

    const handleExtChange = e => {
        const { setFieldValue, values } = ref.current.getContext()
        const val = /^$|^[0-9]+$/.test(e.target.value)
            ? e.target.value : values[EXT.value]

        setFieldValue(EXT.value, val)
    }

    function onReCaptchaHandle(val) {
        if (val) {
            setReCaptcha(val)
            setCaptchaError('')
        }
    }

    const handleExecuteRecaptcha = useCallback(async () => {
        setKey(prevKey => prevKey + 1)
    })

    const handleFieldError = useCallback(async () => {
        console.log('handleFieldError', ref)
        const { validateForm } = ref.current.getContext()
        await validateForm()
        if (captchaError) {
            setCaptchaError(reCaptchaError)
        }
    })

    const handleCancel = useCallback(() => {
        // showLoader()
        history.push(previousPath)
    }, [])

    const handleCountryCodeChange = useCallback(e => {
        setCountryCode(e.target.value)
    }, [])

    const validate = values => {
        let errors = {}
        const { setFieldError } = ref.current.getContext()
        Object.values(formFields).forEach(field => {
            if (!values[field.value] && field.isRequired) {
                errors[field.value] = translate('required')
                errors && setFieldError(field, translate('required'))
            }
            if (values[EMAIL.value] && !/^[a-z0-9!'#$%&*+/=?^_`{|}~-]+(?:\.[a-z0-9!'#$%&*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-zA-Z]{2,}$/i.test(values[EMAIL.value])) {
                errors = { ...errors, [EMAIL.value]: translate('incorrectEmail') }
            }
            if (!values[COUNTRY_CODE.value]) {
                errors = { ...errors, [COUNTRY_CODE.value]: translate('required') }
                errors && setFieldError(COUNTRY_CODE, translate('required'))
                if (!values[PHONE_NUMBER.value]) {
                    errors = { ...errors, [PHONE_NUMBER.value]: translate('required') }
                    errors && setFieldError(PHONE_NUMBER, translate('required'))
                } else {
                    errors = { ...errors, [PHONE_NUMBER.value]: translate('countryCodeRequired') }
                    errors && setFieldError(PHONE_NUMBER, translate('countryCodeRequired'))
                }
            }

            if (values[PHONE_NUMBER.value]) {
                let codeValue = countryCode
                if (values[COUNTRY_CODE.value]) {
                    codeValue = values[COUNTRY_CODE.value].label.split(' ')
                }
                const phone = parsePhoneNumberFromString(codeValue[0] + values[PHONE_NUMBER.value])
                if (phone && !phone.isValid()) {
                    errors = { ...errors, [PHONE_NUMBER.value]: translate('incorrectPhoneNumber') }
                    errors && setFieldError(PHONE_NUMBER, translate('incorrectPhoneNumber'))
                }
                if (!phone) {
                    errors = { ...errors, [PHONE_NUMBER.value]: translate('incorrectPhoneNumber') }
                    errors && setFieldError(PHONE_NUMBER, translate('incorrectPhoneNumber'))
                }
            }

            if (field.value === LANGUAGE.value && !values[LANGUAGE.value]) {
                errors = { ...errors, [LANGUAGE.value]: translate('incorrectlanguage') }
                errors && setFieldError(LANGUAGE, translate('incorrectlanguage'))
            }
        })
        console.log('errors', errors)
        return errors
    }

    return (
        <>
            <div className={classes.loader}>
                <AppBar className={clsx(classes.appBar, {
                    [classes.appBarShift]: !isMobile
                })} position="relative" color="inherit">
                    <Toolbar className={clsx(classes.toolbar, isMobile && classes.toolbarMobile)}>
                        <div className={clsx(classes.logoContainer, { [classes.titleShift]: !mobileDevice })}>
                            {
                                (i18n.language === constants.ENGLISH)
                                    ? <a href="https://www.rbccm.com/en/" target="_blank" rel="noopener noreferrer" alt="rbccm logo">
                                        <img className={classes.logoEn} src={'/img/RBC_Capital_Markets_logo_en.svg?url'} />
                                    </a>
                                    : <a href="https://www.rbccm.com/fr/" target="_blank" rel="noopener noreferrer" alt="rbccm logo">
                                        <img className={classes.logoFr} src={'/img/RBC_Capital_Markets_logo_fr.svg?url'} />
                                    </a>
                            }
                        </div>
                        <LanguageButton clsName={!isMobile ? 'languageButton' : 'mobileLanguageButtonRegister'} component={ANALYTICS_REGISTER} loginTitle={loginTitle} variant='text'></LanguageButton>
                    </Toolbar>
                </AppBar>
            </div>
            <Grid container className={classes.gridContainerCenter}>
                <Grid className={clsx(classes.gridContainerPadding, stepNumber === 1 && classes.gridContainerPaddingSuccess)} lg={6} md={8} sm={12} xs={12}>
                    { stepNumber === 0 && <Paper spacing={2} className={classes.submitRequestContainer}>
                        <div className={classes.titleHeader}>
                            <Typography className={classes.title}>{title}</Typography>
                            {!isMobile && !smallScreen && <img className={classes.MATALogo} src={'/img/analytics/mata_logo.png'} />}
                        </div>
                        <Typography className={classes.verbiage}>{verbiage}</Typography>
                        <Form {...{
                            enableReinitialize: true,
                            initialValues: { ...initialValues },
                            onSubmit: handleSubmit,
                            ref,
                            validate,
                            validateOnBlur: true
                        }}>
                            <Grid container spacing={2} className={classes.formGrid}>
                                <Grid item md={6} sm={6} xs={12}>
                                    <TextField {...{
                                        field: FIRST_NAME.value,
                                        fullWidth: true,
                                        InputLabelProps: { className: classes.inputMataLabelProps },
                                        label: translate('registrationAnalytics.firstname'),
                                        name: FIRST_NAME.text,
                                        placeholder: translate('registrationAnalytics.firstname'),
                                        type: 'text'
                                    }} />
                                </Grid>
                                <Grid item style={isMobile ? { paddingTop: '48px' } : {}} md={6} sm={6} xs={12}>
                                    <TextField {...{
                                        field: LAST_NAME.value,
                                        fullWidth: true,
                                        InputLabelProps: { className: classes.inputMataLabelProps },
                                        label: translate('registrationAnalytics.lastname'),
                                        name: LAST_NAME.text,
                                        placeholder: translate('registrationAnalytics.lastname'),
                                        type: 'text',
                                    }} />
                                </Grid>
                                <Grid item md={6} sm={6} xs={12} style={{ paddingTop: '48px' }} className={classes.paddingContainer}>
                                    <TextField {...{
                                        field: COMPANY_NAME.value,
                                        fullWidth: true,
                                        InputLabelProps: { className: classes.inputMataLabelProps },
                                        label: translate('registrationAnalytics.companyname'),
                                        name: COMPANY_NAME.text,
                                        placeholder: translate('registrationAnalytics.companyname'),
                                        type: 'text'
                                    }} />
                                </Grid>
                                <Grid item md={6} sm={6} xs={12} style={{ paddingTop: '48px' }} className={classes.paddingContainerPhoneNumber}>
                                    <Grid item md={6} sm={4} xs={12} className={classes.countryCodeContainer}>
                                        <Autocomplete classes={{
                                            inputRoot: classes.customInputRoot
                                        }} sx={{ alignItems: 'center', display: 'flex', fontSize: '14px', marginTop: '0px' }} {...{
                                            field: COUNTRY_CODE.value,
                                            fullWidth: true,
                                            getOptionLabel: option => getOptionLabelList(option, countryCodeList),
                                            InputLabelProps: { className: classes.inputCountryCodeProps },
                                            inputValue: countryCode,
                                            label: translate('registrationAnalytics.countryCode'),
                                            name: COUNTRY_CODE.text,
                                            noOptionsText: NO_DATA,
                                            onChange: handleCountryCodeChange,
                                            onInputChange: (event, newInputValue) => {
                                                if (newInputValue) {
                                                    setCountryCode(newInputValue)
                                                } else {
                                                    setCountryCode(defaultCountryCode)
                                                }
                                            },
                                            options: countryCodeList,
                                            placeholder: translate('registrationAnalytics.countryCode'),
                                            size: 'large',
                                            TextFieldProps: { className: classes.inputCountryCodeTextProps, InputLabelProps: { InputProps: { autoComplete: 'off' }, shrink: true } },
                                            variant: 'standard'
                                        }} />
                                    </Grid>
                                    <Grid item md={6} sm={6} xs={12} className={classes.phoneNumberContainer}>
                                        <TextField {...{
                                            autoComplete: 'off',
                                            field: PHONE_NUMBER.value,
                                            fullWidth: true,
                                            InputLabelProps: { className: classes.inputMataLabelProps },
                                            InputProps: { ...{
                                                onChange: handlePhoneNumberChange,
                                            } },
                                            label: translate('registrationAnalytics.phonenumber'),
                                            name: PHONE_NUMBER.text,
                                            placeholder: translate('registrationAnalytics.phonenumber'),
                                            type: 'text'
                                        }} />
                                    </Grid>
                                    <Grid md={4} sm={3} xs={12} className={classes.extContainer} item>
                                        <TextField className={classes.extField} {...{
                                            autoComplete: 'off',
                                            field: EXT.value,
                                            fullWidth: true,
                                            InputLabelProps: { className: classes.inputMataLabelProps },
                                            InputProps: { ...{
                                                onChange: handleExtChange,
                                            } },
                                            label: translate('registrationAnalytics.ext'),
                                            name: EXT.text,
                                            placeholder: translate('registrationAnalytics.ext'),
                                        }}/>
                                    </Grid>
                                </Grid>
                                <Grid item md={6} sm={6} xs={12} style={{ paddingTop: '48px' }} className={classes.paddingContainer}>
                                    <TextField {...{
                                        field: EMAIL.value,
                                        fullWidth: true,
                                        InputLabelProps: { className: classes.inputMataLabelProps },
                                        label: translate('registrationAnalytics.email'),
                                        name: EMAIL.text,
                                        placeholder: translate('registrationAnalytics.email'),
                                        type: 'email'
                                    }} />
                                </Grid>
                                <Grid item md={6} sm={6} xs={12} style={{ paddingTop: '48px' }} className={classes.paddingContainer}>
                                    <Autocomplete {...{
                                        field: LANGUAGE.value,
                                        fullWidth: true,
                                        getOptionLabel: option => getOptionLabel(option),
                                        label: translate('registrationAnalytics.language'),
                                        name: LANGUAGE.text,
                                        noOptionsText: NO_DATA,
                                        options: languageList,
                                        placeholder: translate('registrationAnalytics.language'),
                                        TextFieldProps: { InputLabelProps: { className: classes.inputMataLabelProps }, InputProps: { autoComplete: 'off' } },
                                        variant: 'standard'
                                    }} />
                                </Grid>
                                <Grid item md={12} sm={12} xs={12} className={classes.interestedInContainer}>
                                    <Typography className={classes.interestedInTitle}>{translate('registrationAnalytics.interestedIN')}</Typography>
                                    <div className={(isMobile) ? classes.interestedInMobile : classes.interestedIn}>
                                        {interestedIn.map(item => (
                                            <HorizontalGroup key={item.name} className={classes.interestedInChild} gutter={0}>
                                                <Checkbox id={item.field.value} className={classes.checkboxMain} size='medium' name={item.name} onChange={handleChange}/>
                                                <FormLabel for={item.field.value} className={classes.labelMain}>{item.name}</FormLabel>
                                            </HorizontalGroup>
                                        ))}
                                    </div>
                                </Grid>
                                {siteKey ? <Grid style={isMobile ? { width: '100%' } : {}} item md={6} sm={12} xs={12}>
                                    <ReCAPTCHA key={key} ref={recaptchaRef} sitekey={siteKey} render="explicit" hl={locale}
                                        onChange={onReCaptchaHandle} style={isMobile ? { transform: 'scale(0.85)', transformOrigin: '0 0' } : {}} />
                                    <p style={{ color: '#f44336' }}>{captchaError}</p>
                                </Grid> : <></>}
                                <Grid item md={6} sm={12} xs={12} container className={clsx(classes.buttonRow, isMobile && classes.buttonRowMobileScreen)}>
                                    <HorizontalGroup gutter={10}>
                                        <Button className={classes.cancel} onClick={handleCancel} variant="outlined">{translate('registrationAnalytics.cancel')}</Button>
                                        <Button className={`${classes.submit} ${selectedLanguage === constants.FRENCH ? classes.submitFr : ''}`} type="submit">{translate('registrationAnalytics.submit')}</Button>
                                    </HorizontalGroup>
                                </Grid>
                            </Grid>

                            <p style={{ color: '#f44336' }}>{ errorMessage ? translate('oktaApiResponse.' + `${errorMessage}`) : null}</p>

                        </Form>
                    </Paper>}
                    { (stepNumber === 1) && <SubmitSuccess />}
                </Grid>
            </Grid>
            <Grid container className={classes.gridContainerCenter}>
                <Grid md={6} sm={8} xs={12}>
                    <Footer />
                </Grid>
            </Grid>
        </>
    )

}

Component.displayName = 'analyticsRegister'

Component.defaultProps = {
    stepNumber: 0,
}

Component.propTypes = {
    classes: PropTypes.shape({
        loader: PropTypes.string
    }),
    onRequestRecaptcha: PropTypes.func.isRequired,
    onSubmitRequest: PropTypes.func.isRequired,
    stepNumber: PropTypes.number,
}

export default withStyles(Component, styles)
